import {
  CustomFieldEntityEnum,
  IRateReqSchemaUI,
  OfferingTypesEnum,
  RateBillingFrequencyEnum,
  RateStatusEnum,
  RateUsageBillingFrequencyEnum,
  SubscriptionTimingEnum,
} from '@monetize/types/app';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import { CustomFieldDataForm } from '../../../../../components/CustomFields/CustomFieldDataForm';
import {
  MBox,
  MButton,
  MCurrencySelect,
  MFlex,
  MFormField,
  MGridItem,
  MIcon,
  MInput,
  MLockedTextOrContent,
  MSwitch,
  MText,
  MTextarea,
  MTooltip,
} from '../../../../../components/Monetize';
import { DatePicker } from '../../../../../components/Monetize/DatePicker/DatePicker';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { OFFERINGS } from '../../../../../constants';
import {
  RATE_BILLING_FREQUENCY_MAP,
  getBillingFrequencyItems,
  getRateUsageBillingFrequencyDisplay,
  getSubscriptionTimingDisplay,
  getUsageFrequencyItems,
} from '../../../../../constants/offerings';
import { useCurrencies } from '../../../../../hooks/useCurrencies';
import { useFlags } from '../../../../../services/launchDarkly';
import { getOfferingType } from '../../offeringUtils';
import LockedStatus from './LockedStatus';
import { RateActions } from './RateActions';
import { RateSubscriptionFrequency } from './RateSubscriptionFrequency';

interface RateFormHeaderProps {
  nameRef: React.MutableRefObject<HTMLInputElement | null>;
  loading: boolean;
  isLocked: boolean;
  currency: string;
  rateId: string;
  type: OfferingTypesEnum;
  billingFrequency?: RateBillingFrequencyEnum | null;
  billingFrequencyInMonths?: number | null;
  usageBillingFrequency?: RateUsageBillingFrequencyEnum | null;
  archivedOffering?: boolean;
  subscriptionTiming?: SubscriptionTimingEnum | null;
  handleReset: () => void;
  handleRemove: () => void;
  handleClone: () => void;
  isReadOnly?: boolean;
  isSubscriptionProductTypeExist: boolean;
  isSubscription: boolean;
  isCreditAllocationFrequencyRequired: boolean;
}
export const RateFormHeader = ({
  loading,
  archivedOffering,
  nameRef,
  currency,
  isLocked,
  rateId,
  type,
  billingFrequency,
  billingFrequencyInMonths,
  subscriptionTiming,
  usageBillingFrequency,
  handleClone,
  handleRemove,
  isReadOnly,
  isCreditAllocationFrequencyRequired,
}: RateFormHeaderProps) => {
  const {
    setValue,
    getValues,
    control,
    formState: { errors, dirtyFields },
  } = useFormContext<IRateReqSchemaUI>();

  const { id, options } = getValues();
  const tokenAllocationFrequencyInMonth =
    options?.tokenAllocationFrequencyInMonth;

  const [allFieldsVisibility, setAllFieldsVisibility] = useState(true);
  const subscriptionFrequencyItems = getBillingFrequencyItems(type);
  const { defaultCurrency } = useCurrencies();
  const { isOneTime, isMinCommit, isOnetimeToken } = getOfferingType(type);
  const {
    showQuotableField,
    allowMinCommitAdditionalFrequencies,
    allowSelfServeRate,
    enableTokenCredits,
  } = useFlags();
  const rateUsageFrequencyOptions = getUsageFrequencyItems(type);

  const isMinCommitFrequencyLocked =
    isMinCommit && !allowMinCommitAdditionalFrequencies;

  const showCreditAllocationFrequency =
    enableTokenCredits && isCreditAllocationFrequencyRequired;

  const handleSubscriptionFrequencyChange = (data) => {
    setValue('billingFrequency', data.billingFrequency, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('billingFrequencyInMonths', data.billingFrequencyInMonths, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (isMinCommit) {
      setValue(
        'usageBillingFrequency',
        data.billingFrequency as any as RateUsageBillingFrequencyEnum, // possible billingFrequency on MIN_COMMIT are monthly/quarterly/semi-anually/anually
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }

    if (enableTokenCredits) {
      setValue(
        'options.tokenAllocationFrequencyInMonth',
        data.billingFrequencyInMonths,
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
    }
  };

  return (
    <>
      <MGridItem>
        <MFormField
          labelProps={{ display: 'block' }}
          error={errors?.name}
          label="Rate Name"
          isRequired
        >
          <Controller
            name="name"
            defaultValue=""
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <MInput
                placeholder="New Rate"
                isDisabled={loading || archivedOffering}
                isReadOnly={isReadOnly}
                bgColor="tWhite.base"
                variant={
                  isReadOnly
                    ? 'readonly'
                    : !!rateId && !!rest.value && dirtyFields.name
                      ? 'unsaved'
                      : 'outline'
                }
                ref={(e) => {
                  ref(e);
                  nameRef.current = e;
                }}
                {...rest}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem>
        <MFormField
          labelProps={{ display: 'block' }}
          error={errors?.status}
          label="Status"
          isRequired
        >
          <Controller
            name="status"
            defaultValue={RateStatusEnum.ACTIVE}
            control={control}
            render={({ field }) => (
              <MCustomSelect
                inputProps={{
                  variant: isReadOnly
                    ? 'readonly'
                    : !!rateId && dirtyFields.status
                      ? 'unsaved'
                      : 'outline',
                }}
                isDisabled={loading || archivedOffering}
                isReadOnly={isReadOnly}
                bgColor="tWhite.base"
                placeholder="Status"
                items={OFFERINGS.RATE_STATUS_MODEL_DISPLAY_STRING}
                {...field}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem>
        <MFormField
          labelProps={{ display: 'block' }}
          error={errors?.currency}
          label="Currency"
          isRequired={!isLocked}
          data-testid="rate-currency"
        >
          <MLockedTextOrContent text={currency} h={8} isLocked={isLocked}>
            <Controller
              name="currency"
              defaultValue={defaultCurrency}
              control={control}
              render={({ field }) => (
                <MCurrencySelect
                  inputProps={{
                    variant: isReadOnly
                      ? 'readonly'
                      : !!rateId && dirtyFields.currency
                        ? 'unsaved'
                        : 'outline',
                  }}
                  isDisabled={loading || archivedOffering}
                  isReadOnly={isReadOnly}
                  {...field}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>

      <MGridItem>
        <MFormField
          labelProps={{ display: 'block' }}
          error={errors?.billingFrequency}
          label={
            isMinCommit ? 'Minimum Commit Frequency' : 'Subscription Frequency'
          }
          isRequired={!isLocked && type !== OfferingTypesEnum.ONETIME}
          data-testid="rate-billing-frequency"
          gridColumn={isMinCommit ? 'span 2' : 'span 1'}
        >
          <MLockedTextOrContent
            text={
              billingFrequency
                ? RATE_BILLING_FREQUENCY_MAP[billingFrequency](
                    billingFrequencyInMonths,
                  ).label
                : ''
            }
            h={8}
            isLocked={
              isLocked ||
              isReadOnly ||
              loading ||
              isOneTime ||
              isOnetimeToken ||
              archivedOffering ||
              isMinCommitFrequencyLocked
            }
          >
            <RateSubscriptionFrequency
              isDisabled={
                loading ||
                isOneTime ||
                isOnetimeToken ||
                archivedOffering ||
                isMinCommitFrequencyLocked
              }
              isDirty={!!rateId && dirtyFields.billingFrequency}
              isReadOnly={isReadOnly || isMinCommitFrequencyLocked}
              subscriptionFrequencyItems={subscriptionFrequencyItems}
              control={control}
              data={{
                billingFrequency,
                billingFrequencyInMonths,
              }}
              onChange={handleSubscriptionFrequencyChange}
              disableCustomFrequency={isMinCommitFrequencyLocked}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>
      <MGridItem>
        <MFormField
          labelProps={{ display: 'block' }}
          error={errors?.subscriptionTiming}
          label={isMinCommit ? 'Minimum Commit Timing' : 'Subscription Timing'}
          isRequired={!isLocked && type !== OfferingTypesEnum.ONETIME}
          data-testid="rate-subscription-timing"
        >
          <MLockedTextOrContent
            text={getSubscriptionTimingDisplay(subscriptionTiming)}
            h={8}
            isLocked={isLocked || type === OfferingTypesEnum.ONETIME}
          >
            <Controller
              name="subscriptionTiming"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  inputProps={{
                    variant: isReadOnly
                      ? 'readonly'
                      : !!rateId && dirtyFields.subscriptionTiming
                        ? 'unsaved'
                        : 'outline',
                  }}
                  isDisabled={loading || type === OfferingTypesEnum.ONETIME}
                  isReadOnly={isReadOnly}
                  bgColor="tWhite.base"
                  placeholder="Subscription Timing"
                  items={OFFERINGS.SUBSCRIPTION_TIMING_UI_OPTIONS}
                  {...field}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>

      <MGridItem>
        {!isMinCommit && (
          <MFormField
            labelProps={{ display: 'block' }}
            error={errors?.usageBillingFrequency}
            label="Usage Frequency"
            data-testid="rate-usage-billing-frequency"
          >
            <MLockedTextOrContent
              text={getRateUsageBillingFrequencyDisplay(usageBillingFrequency)}
              h={8}
              isLocked
            >
              {/* this select never appears currently as we have only one usage billing frequency choice */}
              <Controller
                name="usageBillingFrequency"
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MCustomSelect
                    inputProps={{
                      variant:
                        !!rateId && dirtyFields.usageBillingFrequency
                          ? 'unsaved'
                          : 'outline',
                    }}
                    bgColor="tWhite.base"
                    placeholder="Usage Frequency"
                    items={rateUsageFrequencyOptions}
                    value={value}
                    {...rest}
                  />
                )}
              />
            </MLockedTextOrContent>{' '}
          </MFormField>
        )}
      </MGridItem>
      <MGridItem>
        <MFlex justifyContent="flex-end" align="flex-start" mr={-2}>
          {isLocked && <LockedStatus containerProps={{ mt: 1.5, mr: 1 }} />}
          {!isReadOnly && (
            <RateActions
              isLocked={isLocked}
              isNewRate={id?.match('new-') !== null}
              rateId={rateId}
              handleClone={handleClone}
              handleRemove={handleRemove}
            />
          )}
        </MFlex>
      </MGridItem>
      <MBox />
      {allFieldsVisibility && (
        <>
          <MGridItem>
            <MFormField
              labelProps={{ display: 'block' }}
              error={errors?.description}
              label="Description"
            >
              <Controller
                name="description"
                defaultValue=""
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MTextarea
                    variant={
                      isReadOnly
                        ? 'readonly'
                        : !!rateId && !!value && dirtyFields.description
                          ? 'unsaved'
                          : 'outline'
                    }
                    value={value || ''}
                    rows={2}
                    minHeight="30px"
                    isDisabled={loading || archivedOffering}
                    isReadOnly={isReadOnly}
                    {...rest}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem gridColumn="span 2">
            <MFormField
              labelProps={{ display: 'block' }}
              error={errors?.internalDescription}
              label="Internal Description"
              tooltip="This internal description will only be shown when creating a quote. This will not be visible to the customer."
            >
              <Controller
                name="internalDescription"
                defaultValue=""
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MTextarea
                    variant={
                      isReadOnly
                        ? 'readonly'
                        : !!rateId && !!value && dirtyFields.internalDescription
                          ? 'unsaved'
                          : 'outline'
                    }
                    value={value || ''}
                    rows={2}
                    minHeight="30px"
                    isDisabled={loading || archivedOffering}
                    isReadOnly={isReadOnly}
                    {...rest}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem>
            <MFormField
              labelProps={{ display: 'block' }}
              error={errors.startDate}
              label="Start Date"
            >
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    variant={
                      isReadOnly
                        ? 'readonly'
                        : !!rateId && !!field.value && dirtyFields.startDate
                          ? 'unsaved'
                          : 'outline'
                    }
                    placeholder="Select Start Date"
                    clearable
                    isDisabled={loading || archivedOffering}
                    isReadOnly={isReadOnly}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem>
            <MFormField
              labelProps={{ display: 'block' }}
              error={errors.endDate}
              label="End Date"
            >
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    variant={
                      isReadOnly
                        ? 'readonly'
                        : !!rateId && !!field.value && dirtyFields.endDate
                          ? 'unsaved'
                          : 'outline'
                    }
                    placeholder="Select End Date"
                    clearable
                    isDisabled={loading || archivedOffering}
                    isReadOnly={isReadOnly}
                  />
                )}
              />
            </MFormField>
          </MGridItem>

          <MGridItem>
            <MFlex mt="29px">
              {showQuotableField && (
                <MTooltip
                  label="Allow this Rate to be added to Quotes"
                  placement="bottom-start"
                  shouldWrapChildren
                >
                  <MFormField error={errors?.quotable}>
                    <Controller
                      name="quotable"
                      control={control}
                      render={({ field: { value, ...rest } }) => (
                        <MFlex align="center">
                          <MText fontWeight="semibold">Quotable</MText>
                          <MBox mr={2}>
                            <MIcon
                              as={MdInfo}
                              color="tPurple.base"
                              ml="1"
                              w={3.5}
                              h={3.5}
                            />
                          </MBox>
                          <MSwitch
                            data-testid="toggle-quotable"
                            isChecked={value}
                            isDisabled={
                              loading || archivedOffering || isReadOnly
                            }
                            variant={
                              !isReadOnly && !!rateId && dirtyFields.quotable
                                ? 'unsaved'
                                : undefined
                            }
                            {...rest}
                          />
                        </MFlex>
                      )}
                    />
                  </MFormField>
                </MTooltip>
              )}
            </MFlex>
          </MGridItem>
          <MBox />
          <MBox />

          {showCreditAllocationFrequency && (
            <MGridItem gridColumn="span 1">
              <MFormField
                label="Credit allocation frequency (Months)"
                error={errors?.options?.tokenAllocationFrequencyInMonth}
                isRequired={isCreditAllocationFrequencyRequired}
              >
                <MLockedTextOrContent
                  text={tokenAllocationFrequencyInMonth}
                  h={8}
                  isLocked={isLocked}
                >
                  <Controller
                    name="options.tokenAllocationFrequencyInMonth"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        {...rest}
                        value={value ? String(value) : ''}
                        type="number"
                        max={60}
                        isDisabled={loading || archivedOffering}
                        onChange={(e) => {
                          const newValue = Number(e.target.value);
                          if (!isNaN(newValue) && newValue <= 60) {
                            setValue(
                              'options.tokenAllocationFrequencyInMonth',
                              newValue,
                              { shouldDirty: true, shouldValidate: true },
                            );
                          }
                        }}
                      />
                    )}
                  />
                </MLockedTextOrContent>
              </MFormField>
            </MGridItem>
          )}

          {/* Quotable's label-height offset, to line up with the input beside it */}
          <MGridItem mt={showCreditAllocationFrequency ? '29px' : undefined}>
            {allowSelfServeRate && (
              <MTooltip
                label="Allow this Rate to be self serve"
                placement="bottom-start"
                shouldWrapChildren
              >
                <MFormField error={errors?.revenueChannels}>
                  <Controller
                    name="revenueChannels"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MFlex align="center">
                        <MText fontWeight="semibold">Self Serve</MText>
                        <MBox mr={2}>
                          <MIcon
                            as={MdInfo}
                            color="tPurple.base"
                            ml="1"
                            w={3.5}
                            h={3.5}
                          />
                        </MBox>
                        <MSwitch
                          {...rest}
                          data-testid="toggle-revenueChannels"
                          isChecked={value?.includes('SELF_SERVICE')}
                          isDisabled={loading || archivedOffering || isReadOnly}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            const newValue = isChecked
                              ? [...value, 'SELF_SERVICE']
                              : ['SALES'];

                            setValue('revenueChannels', newValue, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                          variant={
                            !isReadOnly &&
                            !!rateId &&
                            dirtyFields.revenueChannels
                              ? 'unsaved'
                              : undefined
                          }
                        />
                      </MFlex>
                    )}
                  />
                </MFormField>
              </MTooltip>
            )}
          </MGridItem>

          <MGridItem gridColumn="2 / -1">
            <Controller
              name="customFields"
              control={control}
              render={({ field: { value, onChange } }) => (
                <CustomFieldDataForm
                  entity={CustomFieldEntityEnum.RATE}
                  value={value || {}}
                  setValue={onChange}
                  fieldColSpan={4}
                  mode="page"
                  isReadOnly={isReadOnly}
                  showDivider
                />
              )}
            />
          </MGridItem>
        </>
      )}
      {allFieldsVisibility && (
        <>
          <MBox />
          <MBox />
          <MBox />
          <MBox />
          <MBox />
          <MBox />
        </>
      )}
      <MBox gridColumn="2">
        <MButton
          variant="tertiary"
          fontWeight="600"
          ml={-6}
          onClick={() => {
            setAllFieldsVisibility((visibility) => !visibility);
          }}
        >
          {allFieldsVisibility ? 'Show Fewer Fields' : 'Show More Fields'}
        </MButton>
      </MBox>
    </>
  );
};
